import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';

import Swal from 'sweetalert2';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Trash2,
  Download
} from 'lucide-react';

function MembershipList() {
  const [passes, setPasses] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [campaignSearchTerm, setCampaignSearchTerm] = useState('All Campaigns');
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const campaignInputRef = useRef(null);
  const campaignDropdownRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 10;

  // Detail modal
  const [selectedPass, setSelectedPass] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    fetchPasses();
  }, [currentPage, selectedCampaign, searchTerm]);

  const isAllCampaigns = !campaignSearchTerm.trim() || campaignSearchTerm.trim() === 'All Campaigns';
  const filteredCampaigns = (() => {
    if (isAllCampaigns) return campaigns.slice(0, 50);
    const search = campaignSearchTerm.toLowerCase();
    return campaigns.filter(c => c.name?.toLowerCase().includes(search)).slice(0, 10);
  })();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        campaignDropdownRef.current &&
        !campaignDropdownRef.current.contains(event.target) &&
        campaignInputRef.current &&
        !campaignInputRef.current.contains(event.target)
      ) {
        setShowCampaignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedCampaign && campaigns.length > 0) {
      const campaign = campaigns.find(c => c.id.toString() === selectedCampaign.toString());
      if (campaign && campaignSearchTerm !== campaign.name) setCampaignSearchTerm(campaign.name);
    } else if (!selectedCampaign) {
      setCampaignSearchTerm('All Campaigns');
    }
  }, [selectedCampaign, campaigns]);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/admin/campaigns', { params: { limit: 500 } });
      setCampaigns(response.data.campaigns || []);
    } catch (err) {
      console.error('Fetch campaigns error:', err);
      setCampaigns([]);
    }
  };

  const fetchPasses = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: currentPage, limit: PAGE_SIZE };
      if (selectedCampaign) params.campaignId = selectedCampaign;
      if (searchTerm) params.search = searchTerm;

      const response = await api.get('/admin/membership', { params });
      setPasses(response.data.passes || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Fetch passes error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load membership passes');
      setPasses([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  const handleCampaignSelect = (campaignId, campaignName) => {
    setSelectedCampaign(campaignId);
    setCampaignSearchTerm(campaignName);
    setShowCampaignDropdown(false);
    setCurrentPage(1);
  };

  const handleCampaignClear = () => {
    setSelectedCampaign('');
    setCampaignSearchTerm('All Campaigns');
    setShowCampaignDropdown(false);
    setCurrentPage(1);
  };

  const handleCampaignInputChange = (e) => {
    setCampaignSearchTerm(e.target.value);
    setShowCampaignDropdown(true);
    if (!e.target.value.trim() || e.target.value.trim() === 'All Campaigns') setSelectedCampaign('');
  };

  const openDetailModal = (pass) => {
    setSelectedPass(pass);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setSelectedPass(null);
    setShowDetailModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleDeletePass = async (pass) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete the membership pass for "<strong>${pass.member_name || pass.member_email}</strong>".<br><br>This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/membership/${pass.id}`);
        await Swal.fire('Deleted!', 'The membership pass has been deleted.', 'success');
        fetchPasses();
        if (showDetailModal && selectedPass?.id === pass.id) closeDetailModal();
      } catch (err) {
        await Swal.fire('Error!', err.response?.data?.error || 'Failed to delete the pass.', 'error');
      }
    }
  };

  const handleDownload = (pass) => {
    if (!pass.pass_file_path) {
      Swal.fire('Not Available', 'The .pkpass file has not been generated for this member yet. Please configure Apple Wallet certificates in backend/certs/ first.', 'info');
      return;
    }
    window.open(`${process.env.REACT_APP_API_URL || ''}/api/admin/membership/${pass.id}/download`, '_blank');
  };

  const exportToCSV = () => {
    const headers = ['Member #', 'Name', 'Email', 'Campaign', 'Pass File', 'Date Issued'];
    const rows = passes.map(p => [
      p.member_number || '',
      p.member_name || '',
      p.member_email || '',
      p.campaign_name || '',
      p.pass_file_path ? 'Generated' : 'Pending certs',
      formatDate(p.created_at)
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `membership_passes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AdminLayout activeSection="MEMBERSHIP">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-white mb-6">MEMBERSHIP</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Campaign Filter */}
          <div className="relative" ref={campaignDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Campaign</label>
            <div className="flex">
              <input
                ref={campaignInputRef}
                type="text"
                value={campaignSearchTerm}
                onChange={handleCampaignInputChange}
                onFocus={() => campaigns.length > 0 && setShowCampaignDropdown(true)}
                placeholder="Search campaigns..."
                className={`flex-1 px-3 py-2 border border-gray-300 ${selectedCampaign ? 'rounded-l-md' : 'rounded-md'} focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500`}
              />
              {selectedCampaign && (
                <button type="button" onClick={handleCampaignClear} className="flex items-center px-3 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Clear campaign filter">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {showCampaignDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {filteredCampaigns.length === 0 && !isAllCampaigns ? (
                  <div className="px-4 py-3 text-sm text-gray-500">No campaigns found</div>
                ) : (
                  <>
                    {isAllCampaigns && (
                      <button type="button" onClick={() => handleCampaignSelect('', 'All Campaigns')} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!selectedCampaign ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'}`}>
                        All Campaigns
                      </button>
                    )}
                    {filteredCampaigns.map(c => (
                      <button key={c.id} type="button" onClick={() => handleCampaignSelect(c.id, c.name)} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCampaign === c.id?.toString() ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'}`}>
                        {c.name}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <form onSubmit={handleSearch} className="flex">
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Name, email, or member #..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-r-md hover:bg-teal-700 font-medium text-sm">
                Search
              </button>
            </form>
          </div>

          {/* Export */}
          <div className="flex items-end">
            <button onClick={exportToCSV} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm">
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : passes.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No membership passes found.</div>
        ) : (
          <>
            <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center">
              <span className="text-sm text-gray-600">{total} total pass{total !== 1 ? 'es' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Member #', 'Name', 'Email', 'Campaign', 'Pass File', 'Date Issued', 'Actions'].map(h => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {passes.map(pass => (
                    <tr key={pass.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">{pass.member_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pass.member_name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{pass.member_email || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{pass.campaign_name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pass.pass_file_path ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {pass.pass_file_path ? 'Generated' : 'Pending certs'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(pass.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openDetailModal(pass)} className="text-teal-600 hover:text-teal-900" title="View details">
                            <Eye className="h-5 w-5" />
                          </button>
                          <button onClick={() => handleDownload(pass)} className="text-blue-600 hover:text-blue-900" title="Download .pkpass">
                            <Download className="h-5 w-5" />
                          </button>
                          <button onClick={() => handleDeletePass(pass)} className="text-red-600 hover:text-red-900" title="Delete">
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                  </button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedPass && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={closeDetailModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-teal-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Membership Pass Details</h2>
              <button onClick={closeDetailModal} className="text-white hover:text-teal-200">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Card preview */}
            <div className="m-6 rounded-xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(135deg, #006633 0%, #004d26 100%)' }}>
              <div className="px-5 pt-5 pb-2 flex justify-between items-start">
                <span className="text-white text-xs font-semibold opacity-70 uppercase tracking-wider">{selectedPass.campaign_name || 'Membership'}</span>
                <div className="text-right">
                  <div className="text-white text-xs opacity-70 uppercase tracking-wider">MEMBER #</div>
                  <div className="text-white font-mono font-bold text-sm">{selectedPass.member_number}</div>
                </div>
              </div>
              <div className="px-5 py-3">
                <div className="text-white text-xs opacity-70 uppercase tracking-wider mb-1">NAME</div>
                <div className="text-white font-semibold text-lg">{selectedPass.member_name || '—'}</div>
              </div>
              <div className="bg-white mx-5 mb-5 rounded-md px-4 py-3 flex items-center justify-center">
                <div className="text-center">
                  <div className="font-mono text-xs tracking-widest text-gray-800">{selectedPass.member_number}</div>
                  <div className="mt-1 h-10 flex items-center justify-center gap-px">
                    {selectedPass.member_number.split('').map((c, i) => (
                      <div key={i} className="bg-gray-900" style={{ width: i % 3 === 0 ? 2 : 1, height: i % 5 === 0 ? 36 : 28 }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="px-6 pb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Email</span>
                  <div className="font-medium text-gray-900">{selectedPass.member_email || '—'}</div>
                </div>
                <div>
                  <span className="text-gray-500">Campaign</span>
                  <div className="font-medium text-gray-900">{selectedPass.campaign_name || '—'}</div>
                </div>
                <div>
                  <span className="text-gray-500">Pass File</span>
                  <div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${selectedPass.pass_file_path ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {selectedPass.pass_file_path ? 'Generated' : 'Pending certs'}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Date Issued</span>
                  <div className="font-medium text-gray-900">{formatDate(selectedPass.created_at)}</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => handleDownload(selectedPass)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm">
                <Download className="h-4 w-4" />
                Download .pkpass
              </button>
              <button onClick={() => { closeDetailModal(); handleDeletePass(selectedPass); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default MembershipList;
